import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import { iconAlert } from '@sorare/core/src/atoms/icons/system/iconAlert.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  BrandS,
  HeadlineL,
  HeadlineS,
} from '@sorare/core/src/atoms/typography';
import { Alert } from '@sorare/core/src/atoms/ui/Alert';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { Dialog } from '@sorare/core/src/components/dialog';
import { CardsCarousel } from '@sorare/core/src/components/landing/CollectCards/CardsCarousel';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';
import { absCenter } from '@sorare/core/src/lib/style';

import { RarityVaultBanner } from '#src/components/sealing/RarityVaultBanner/index.tsx';
import bg from '#src/components/sealing/assets/dialog-bg.webp';
import { TokenTransferValidator } from '#src/components/token/TokenTransferValidator/index.tsx';

import { DelayLoadingButton } from './DelayLoadingButton.tsx';
import { LineupCancelationDialog } from './LineupCancelationDialog.tsx';
import type {
  SealCardsDialog_anyCard,
  SealCardsDialog_rewardBoost,
  SealCardsDialog_sealingConfig,
} from './__generated__/index.graphql.ts';

const Root = styled(Dialog)`
  background: url(${bg}) no-repeat center center;
  background-size: cover;
  &:not(.fullScreen) & {
    outline: 1px solid var(--c-nd-150) !important;
    border-radius: var(--double-unit) !important;
  }
`;
const InnerDialog = styled(Vertical).attrs({ center: true, gap: 4 })`
  padding: var(--double-unit) var(--triple-unit);
  .fullScreen & {
    min-height: 100%;
  }
`;
const CardsContainer = styled.div`
  padding: 0 var(--quadruple-unit);
  position: relative;
`;
const WarningContainer = styled(Vertical)`
  ${absCenter}
`;
const WarningPadding = styled.div`
  padding: var(--intermediate-unit) var(--double-unit);
  text-wrap: nowrap;
`;
const NoWrap = styled.span`
  white-space: nowrap;
`;
const YellowText = styled.strong`
  color: var(--c-yellow-400);
`;
const RedText = styled.strong`
  color: var(--c-red-300);
`;
const RedWarningWrapper = styled(Horizontal)`
  border-radius: var(--unit);
  padding: var(--intermediate-unit);
  background: rgba(var(--c-rgb-red-400), 0.1);
  border: 1px solid var(--c-red-300);
  color: var(--c-white);
`;

type Props = {
  open: boolean;
  rarity?: string;
  cards: SealCardsDialog_anyCard[];
  sealingConfig?: SealCardsDialog_sealingConfig;
  rewardBoost?: SealCardsDialog_rewardBoost;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  sealing: boolean;
};

export const SealCardsDialog = ({
  open,
  cards,
  sealingConfig,
  rewardBoost,
  onClose,
  onConfirm,
  loading,
  sealing,
}: Props) => {
  const [lineupCancelationDialogOpen, setLineupCancelationDialogOpen] =
    useState(false);
  const isLaptop = useIsTabletAndAbove();
  const sealableForSum = cards.reduce<number>((acc, card) => {
    return acc + (card.sealableFor || 0);
  }, 0);

  return (
    <TokenTransferValidator
      cards={cards}
      transferContext="seal"
      shouldValidate={open}
    >
      {({ cardsUsage, validationMessages, loading: validationLoading }) => {
        const liveLineupsCount = cardsUsage.filter(
          card => card.liveLineup
        ).length;
        const openedLineupsCount = cardsUsage.reduce((acc, card) => {
          return acc + card.openedLineups.length;
        }, 0);
        const affectedLineupsCount = liveLineupsCount + openedLineupsCount;
        return (
          <Root
            maxWidth="xs"
            fullWidth
            open={open}
            onClose={onClose}
            hideHeader={!isLaptop}
            footer={
              open ? (
                <>
                  {Object.values(validationMessages).length > 1 ? (
                    <RedWarningWrapper>
                      <Vertical gap={0}>
                        <HeadlineS>
                          <Horizontal as="span" gap={0.5}>
                            <FontAwesomeIcon
                              icon={iconAlert}
                              color="var(--c-red-300)"
                            />
                            <FormattedMessage defaultMessage="Warning" />
                          </Horizontal>
                        </HeadlineS>
                        <BodyS className="text-left">
                          <FormattedMessage
                            defaultMessage="Sealing these cards will <strong>cancel multiple lineups</strong>."
                            values={{
                              strong: chunks => <RedText>{chunks}</RedText>,
                            }}
                          />
                        </BodyS>
                      </Vertical>
                      <Button
                        size="small"
                        color="secondary"
                        onClick={() => setLineupCancelationDialogOpen(true)}
                      >
                        <FormattedMessage defaultMessage="See details" />
                      </Button>
                    </RedWarningWrapper>
                  ) : (
                    Object.values(validationMessages).map(
                      (warningMessage, index) => (
                        // oxlint-disable-next-line react/no-array-index-key
                        <Vertical key={index}>{warningMessage}</Vertical>
                      )
                    )
                  )}
                  <Vertical gap={2} style={{ marginTop: 'var(--unit)' }}>
                    <Alert
                      title="Warning"
                      message={
                        <Vertical>
                          {!!sealingConfig?.unsealCooldownDays && (
                            <BodyS>
                              <FormattedMessage
                                defaultMessage="You’ll need to wait {unsealCooldownDays, plural, one {# day} other {# days}} to unseal your card."
                                values={{
                                  unsealCooldownDays:
                                    sealingConfig?.unsealCooldownDays,
                                }}
                              />
                            </BodyS>
                          )}
                          <BodyS>
                            {sealingConfig?.unsealCooldownDays === 0 ? (
                              <FormattedMessage
                                defaultMessage="Sealed cards cannot be <strong>bought, listed, sold, or used in lineups or missions</strong>. You can Unseal up to {unsealsPerPeriod, plural, one {# card} other {# cards}} every {unsealPeriodDays, plural, one {# day} other {# days}} per scarcity."
                                values={{
                                  strong: chunks => (
                                    <YellowText>{chunks}</YellowText>
                                  ),
                                  unsealsPerPeriod:
                                    sealingConfig?.unsealsPerPeriod,
                                  unsealPeriodDays:
                                    sealingConfig?.unsealPeriodDays,
                                }}
                              />
                            ) : (
                              <FormattedMessage
                                defaultMessage="Sealed cards cannot be <strong>bought, listed, sold, or used in lineups or missions</strong>. You can Unseal up to {unsealsPerPeriod, plural, one {# card} other {# cards}} every {unsealPeriodDays, plural, one {# day} other {# days}} per scarcity. After being sealed, each card has a {unsealCooldownDays}-day cooldown before it can be unsealed."
                                values={{
                                  strong: chunks => (
                                    <YellowText>{chunks}</YellowText>
                                  ),
                                  unsealsPerPeriod:
                                    sealingConfig?.unsealsPerPeriod,
                                  unsealPeriodDays:
                                    sealingConfig?.unsealPeriodDays,
                                  unsealCooldownDays:
                                    sealingConfig?.unsealCooldownDays,
                                }}
                              />
                            )}
                          </BodyS>
                        </Vertical>
                      }
                    />
                    <Horizontal gap={1.5}>
                      <Button
                        disabled={loading || sealing}
                        size="medium"
                        onClick={onClose}
                        color="quaternary"
                        fullWidth
                      >
                        <FormattedMessage {...glossary.cancel} />
                      </Button>
                      <DelayLoadingButton
                        delay={5000}
                        loading={loading || sealing || validationLoading}
                        size="medium"
                        color="primary"
                        onClick={onConfirm}
                        fullWidth
                        startIcon={
                          Object.keys(validationMessages).length > 0 ? (
                            <FontAwesomeIcon icon={iconAlert} />
                          ) : (
                            <Vault size={2} action="vault" />
                          )
                        }
                      >
                        <FormattedMessage defaultMessage="Seal" />
                      </DelayLoadingButton>
                    </Horizontal>
                  </Vertical>
                  <LineupCancelationDialog
                    open={lineupCancelationDialogOpen}
                    onClose={() => setLineupCancelationDialogOpen(false)}
                    validationMessages={validationMessages}
                  />
                </>
              ) : null
            }
          >
            {!loading && (
              <InnerDialog className="text-center">
                <HeadlineL>
                  <FormattedMessage
                    defaultMessage="Are you sure you want to seal <nowrap>{cardsCount, plural, one {this card} other {# cards}}?</nowrap>"
                    values={{
                      cardsCount: cards.length,
                      nowrap: (chunks: ReactNode[]) => {
                        return <NoWrap>{chunks}</NoWrap>;
                      },
                    }}
                  />
                </HeadlineL>
                <CardsContainer>
                  <CardsCarousel
                    cards={cards.map(card => ({
                      src: proxyUrl(card.pictureUrl || '', {
                        cropWidth: 160,
                      }),
                      alt: `${card.anyPlayer?.displayName}${card.rarityTyped ? ` - ${card.rarityTyped}` : ''}`,
                    }))}
                    slotSize={160}
                  />
                  <WarningContainer>
                    <Bezel
                      background="var(--c-nd-50-opaque)"
                      boxShadow="inset 0 0 6px var(--c-yellow-400)"
                      borderColor="var(--c-yellow-400)"
                    >
                      <WarningPadding>
                        <BrandS uppercase>
                          <Horizontal gap={1.5} center>
                            <FontAwesomeIcon
                              icon={iconAlert}
                              color="var(--c-yellow-400)"
                            />
                            <FormattedMessage defaultMessage="Cannot be played" />
                            <FontAwesomeIcon
                              icon={iconAlert}
                              color="var(--c-yellow-400)"
                            />
                          </Horizontal>
                        </BrandS>
                      </WarningPadding>
                    </Bezel>
                    {!!affectedLineupsCount && (
                      <Bezel
                        background="var(--c-nd-50-opaque)"
                        boxShadow="inset 0 0 6px var(--c-red-400)"
                        borderColor="var(--c-red-400)"
                      >
                        <WarningPadding>
                          <BrandS uppercase className="text-center">
                            <Horizontal gap={1.5} center>
                              <FontAwesomeIcon
                                icon={iconAlert}
                                color="var(--c-red-400)"
                              />
                              <FormattedMessage
                                defaultMessage="Affects {count, plural, one {# lineup} other {# lineups}}"
                                values={{
                                  count: affectedLineupsCount,
                                }}
                              />
                              <FontAwesomeIcon
                                icon={iconAlert}
                                color="var(--c-red-400)"
                              />
                            </Horizontal>
                          </BrandS>
                        </WarningPadding>
                      </Bezel>
                    )}
                  </WarningContainer>
                </CardsContainer>
                {cards.length > 0 && (
                  <RarityVaultBanner
                    rarity={cards[0].rarityTyped}
                    points={rewardBoost?.points}
                    additionalPoints={sealableForSum}
                    fullRarityVaultMatrix={sealingConfig?.fullRarityVaultMatrix}
                    showHowItWorksCta={false}
                    withBackground={false}
                  />
                )}
              </InnerDialog>
            )}
          </Root>
        );
      }}
    </TokenTransferValidator>
  );
};

SealCardsDialog.fragments = {
  anyCard: gql`
    fragment SealCardsDialog_anyCard on AnyCardInterface {
      slug
      rarityTyped
      pictureUrl
      sealableFor
      anyPlayer {
        slug
        displayName
      }
      ...TokenTransferValidator_anyCard
    }
    ${TokenTransferValidator.fragments.anyCard}
  ` as TypedDocumentNode<SealCardsDialog_anyCard>,
  sealingConfig: gql`
    fragment SealCardsDialog_sealingConfig on SealingConfig {
      unsealCooldownDays
      unsealsPerPeriod
      unsealPeriodDays
      ...RarityVaultBanner_sealingConfig
    }
    ${RarityVaultBanner.fragments.sealingConfig}
  ` as TypedDocumentNode<SealCardsDialog_sealingConfig>,
  rewardBoost: gql`
    fragment SealCardsDialog_rewardBoost on RewardBoost {
      ...RarityVaultBanner_rewardBoost
    }
    ${RarityVaultBanner.fragments.rewardBoost}
  ` as TypedDocumentNode<SealCardsDialog_rewardBoost>,
};
